import { useEffect, Suspense } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useLocation } from 'react-router-dom'
import { createPortal } from 'react-dom'
import { SkipLink } from './SkipLink'
import { Footer } from './Footer'
import { SideNav } from '../chrome/SideNav'
import { LogoMenu } from '../chrome/LogoMenu'
import { AvatarMenu } from '../chrome/AvatarMenu'
import { SearchTrigger } from '../chrome/SearchTrigger'
import { ShortcutsPanel } from '../chrome/ShortcutsPanel'
import { MobileLayout } from '../mobile/MobileLayout'
import { useKeyboardShortcuts } from '../../hooks/use-keyboard-shortcuts'
import { useModal } from '../../hooks/use-modal'
import { useMediaQuery } from '../../hooks/use-media-query'
import { usePageHeader } from '../../providers/PageHeaderProvider'
import { useOrbSignals } from '../../hooks/use-orb-signals'
import { PacerOrb } from '@swim/ui'
import { useA11y } from '@swim/a11y'
import { PacerSheet } from '../pacer/PacerSheet'
import { ToteSheet } from '../tote/ToteSheet'
import { AppBanner } from './AppBanner'
import { RouteLoadingFallback } from './RouteLoadingFallback'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip'
import { ShoppingBag } from 'lucide-react'
import { useToteStore } from '../../stores/tote'
import '@swim/a11y'
import { A11yWidget } from '@swim/a11y'

/**
 * Concept 1 — True 3-column locked layout.
 *
 * ┌────────────┬──────────────────────────────┬────────────┐
 * │ Left col   │ Middle col                   │ Right col  │
 * │ 80px       │ 1fr                          │ 80px       │
 * │ overflow:  │ ┌──────────────────────────┐ │ overflow:  │
 * │ visible    │ │ Header (h-16, no bg)     │ │ visible    │
 * │            │ ├──────────────────────────┤ │            │
 * │ Logo       │ │ Scrollable content       │ │ Pacer      │
 * │   (top)    │ │ ↕ only this moves        │ │ Create     │
 * │            │ │                          │ │ Docs       │
 * │ SideNav    │ └──────────────────────────┘ │            │
 * │ (center)   │                              │   (flex-1) │
 * │            │                              │            │
 * │ Avatar     │                              │ Search     │
 * │ (bottom)   │                              │ (bottom)   │
 * └────────────┴──────────────────────────────┴────────────┘
 *
 * - Root is h-screen overflow-hidden — the building. Background is fixed.
 * - Left/right cols: overflow-visible — tooltips, flyouts, search expansion never clip.
 * - Middle col: flex-1 overflow-y-auto — ONLY this scrolls.
 * - All positions use logical properties (start-/end-) for RTL support.
 * - Z-index stack: content (base) → chrome cols (z-10) → flyouts (z-20) → modals (z-50).
 */
const AUTH_ROUTES = ['/sign-in', '/sign-up', '/forgot-password', '/reset-password', '/verify-email']

export function Layout() {
  const { t } = useTranslation('common')
  const isDesktop = useMediaQuery('(min-width: 1024px)')
  const location = useLocation()
  // Real traffic hits the locale-prefixed path (e.g. /us/en/sign-in), never the
  // bare path — .endsWith matches both that and the subdomain/ChromelessLayout
  // bare-path case, where .has(location.pathname) matched nothing on real routes.
  const isAuthRoute = AUTH_ROUTES.some((route) => location.pathname.endsWith(route)) || location.pathname.endsWith('/age-confirm')
  const isOfferDetail = /\/offers\//.test(location.pathname)
  const isDropsBuy = /\/drops\/buy$/.test(location.pathname)
  const { items: toteItems, openTote } = useToteStore()
  const pacerModal = useModal()
  const shortcutsModal = useModal()
  const { title: pageTitle, action: pageAction, titleIcon: pageTitleIcon } = usePageHeader()
  const signals = useOrbSignals()
  const { prefs: a11yPrefs } = useA11y()

  useKeyboardShortcuts([
    {
      key: 'p',
      handler: () => pacerModal.open(),
      description: 'P — Open Pacer',
    },
    {
      key: 'p',
      meta: true,
      handler: () => pacerModal.open(),
      description: '⌘P — Open Pacer (alt)',
    },
    {
      key: '?',
      handler: () => shortcutsModal.open(),
      description: '? — Keyboard shortcuts',
    },
    {
      key: 'Escape',
      handler: () => {
        pacerModal.close()
        shortcutsModal.close()
      },
      description: 'Esc — Close active panel',
    },
  ])

  useEffect(() => {
    const main = document.getElementById('main-content')
    if (main) {
      main.scrollTo({ top: 0, behavior: 'instant' })
      main.focus()
    }
  }, [location.pathname])

  // ── Mobile shell (< 1024px) ──────────────────────────────────────────────
  if (!isDesktop) {
    return (
      <div className="h-screen overflow-hidden bg-sand-200 dark:bg-slate-900 bg-dot-pattern flex flex-col">
        <SkipLink />
        <MobileLayout />
        <ShortcutsPanel open={shortcutsModal.isOpen} onClose={shortcutsModal.close} />
      </div>
    )
  }

  // ── Desktop shell (≥ 1024px) ──────────────────────────────────────────────
  return (
    <div className="h-screen overflow-hidden bg-sand-200 dark:bg-slate-900 bg-dot-pattern">
      <SkipLink />

      <div className="grid grid-cols-[80px_1fr_80px] h-full">

        {/* LEFT COLUMN — Logo (start) → SideNav (centered) → Avatar (start) */}
        <div className={`relative flex flex-col py-4 overflow-visible z-30 transition-opacity duration-300 ${isAuthRoute ? 'opacity-25 pointer-events-none' : ''}`}>
          {/* Logo — start-aligned so wordmark expands rightward into overflow-visible area */}
          <div className="ps-4">
            <LogoMenu />
          </div>
          {/* SideNav — start-aligned so hover panel expands rightward */}
          <div className="flex-1 flex flex-col items-start justify-center ps-5">
            <SideNav />
          </div>
          {/* Avatar — start-aligned, menu flies rightward */}
          <div className="ps-6">
            <AvatarMenu />
          </div>
        </div>

        {/* MIDDLE COLUMN — Locked header + scrollable content */}
        <div className="flex flex-col h-full overflow-hidden">
          {/* Transparent header row — only rendered when there's a title or action.
              Reserve this space for page-level alerts / banners in the future. */}
          {(pageTitle || pageAction) && (
            <div className={`h-16 flex-shrink-0 flex items-center justify-between px-4 pointer-events-none transition-opacity duration-300 ${isAuthRoute ? 'opacity-25' : ''}`}>
              <div className="w-24" />
              <div className="flex items-center gap-2 pointer-events-auto">
                {pageTitleIcon && (
                  <span className="text-ocean-500 dark:text-ocean-400 flex-shrink-0">{pageTitleIcon}</span>
                )}
                <h1 className="font-display text-2xl font-semibold text-slate-800 dark:text-sand-200">
                  {pageTitle.trim() ? pageTitle : ''}
                </h1>
                {pageAction}
              </div>
              <div className="w-24" />
            </div>
          )}

          {/* Contextual banner — verify email nudge, future: maintenance / feature flags */}
          <AppBanner />

          {/* Scrollable content canvas — only this moves */}
          <main
            id="main-content"
            tabIndex={-1}
            className="flex-1 overflow-y-auto outline-none"
          >
            {!isAuthRoute && (
              // h-full gives the flex child a definite parent height so pages using
              // h-full + overflow-hidden (FeaturedOffers, StreamsPage, StreamPage)
              // get a real pixel value — their inner scroll works and the filter bar stays sticky.
              <div
                key={location.pathname}
                className={`flex flex-col h-full mx-auto max-w-7xl px-4 md:px-6 rounded-2xl transition-[padding] duration-200 pt-4${isOfferDetail ? ' overflow-clip' : ''} ${a11yPrefs.motion === 'reduced' ? '' : 'animate-page-enter'}`}
              >
                <div className={isOfferDetail ? 'flex-1 min-h-0' : 'flex-1'}>
                  <Suspense fallback={<RouteLoadingFallback />}>
                    <Outlet />
                  </Suspense>
                </div>
                {!isOfferDetail && !isDropsBuy && <Footer />}
              </div>
            )}
          </main>

          {/* Auth routes (sign-in, sign-up, etc.) render via portal to document.body.
              Non-auth pages get wrapped in a div with the `animate-page-enter` CSS
              animation (transform: translateY). Per the CSS spec, ANY non-`none`
              transform on an ancestor — even the identity transform left behind
              once that animation finishes — creates a new containing block for
              descendant `position: fixed` elements, so a fixed-inset overlay
              rendered inside that wrapper gets boxed into the wrapper's bounds
              instead of the viewport. Auth pages use fixed-inset full-screen
              overlays (SignInPage.tsx etc.), so they must render outside that
              wrapper entirely — hence the portal, not just an isAuthRoute
              conditional. Do not move this Outlet back inside <main> without
              re-verifying full-viewport coverage on a real page load (lint/
              typecheck do not catch this — it's a runtime layout bug). */}
          {isAuthRoute && createPortal(<Suspense fallback={null}><Outlet /></Suspense>, document.body)}
        </div>

        {/* RIGHT COLUMN — Pacer / Create / Docs (top) → Search (bottom) */}
        <div className={`relative flex flex-col items-center py-4 overflow-visible z-20 transition-opacity duration-300 ${isAuthRoute ? 'opacity-25 pointer-events-none' : ''}`}>
          <div className="flex flex-col items-center gap-3">
            <TooltipProvider delayDuration={400}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <PacerOrb
                      isOpen={pacerModal.isOpen}
                      onOpen={() => pacerModal.open()}
                      onClose={() => pacerModal.close()}
                      asInline
                      hidePanel
                      signals={signals ?? undefined}
                      reducedMotion={a11yPrefs.motion === 'reduced'}
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="left" sideOffset={12}>
                  {t('chrome.pacer')} <kbd className="ms-1 px-1.5 py-0.5 rounded border border-white/30 dark:border-slate-800/30 text-[9px] font-mono leading-none opacity-80">P</kbd>
                </TooltipContent>
              </Tooltip>

              {/* Shopping Tote — ghost button under Pacer */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={openTote}
                    aria-label={`${t('tote.title')}${toteItems.length > 0 ? ` (${toteItems.length})` : ''}`}
                    className="relative w-10 h-10 flex items-center justify-center rounded-xl text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-sand-300/60 dark:hover:bg-slate-700/60 transition-colors"
                  >
                    <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
                    {toteItems.length > 0 && (
                      <span className="absolute top-1.5 right-1.5 min-w-[14px] h-[14px] rounded-full bg-ocean-500 text-white text-[9px] font-bold flex items-center justify-center px-0.5 leading-none">
                        {toteItems.length}
                      </span>
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="left" sideOffset={12}>
                  {t('tote.title')}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          {/* Spacer — pushes search to the bottom */}
          <div className="flex-1" />

          <SearchTrigger />
        </div>

      </div>

      <ShortcutsPanel open={shortcutsModal.isOpen} onClose={shortcutsModal.close} />
      <PacerSheet open={pacerModal.isOpen} onClose={() => pacerModal.close()} />
      <ToteSheet />

      <A11yWidget bottomOffset={80} />
    </div>
  )
}
